import { endOfDay, isBefore, isSameDay } from 'date-fns';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { halfHour } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { DayPartSections } from 'shared/types/page-sections';
import { Task } from 'shared/types/task';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';
import { createPlannedOccurrenceForDate } from 'shared/utils/schedules';

import { useDayPartTree } from '../../hooks/use-day-part-tree';
import { GroupedListProps } from '../../types';
import { DayPartHeader } from '../day-part-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './day-part-grouping-tasks-list.style';

const taskTreeItemTypes = [TreeItemTypes.Task];

const getDayPartStartTime = (dayPart: DayPartSections): number | null => {
  switch (dayPart) {
    case DayPartSections.AllDay:
      return null;
    case DayPartSections.Morning:
      return 9 * 60 * 60 * 1000; // 9am
    case DayPartSections.Afternoon:
      return 12 * 60 * 60 * 1000; // 12pm
    case DayPartSections.Evening:
      return 18 * 60 * 60 * 1000; // 6pm
    case DayPartSections.Overdue:
      return null;
    default:
      return null;
  }
};

export const DayPartGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useDayPartTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const oldTreeData = useMemo(() => structuredClone(treeData), [treeData]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];

    // Process each task to detect section changes
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }

        const task = tasksMap[item.id];
        const taskToUpdate = structuredClone(task);

        // Find which section this task is now in
        const newSectionItem = updatedItems.find((section) =>
          section.children?.includes(item.id),
        );
        const oldSectionItem = Object.values(oldTreeData).find((section) =>
          section.children?.includes(item.id),
        );

        if (!newSectionItem || !oldSectionItem) {
          return;
        }

        const newSectionId = newSectionItem.id as DayPartSections;
        const oldSectionId = oldSectionItem.id as DayPartSections;

        // Skip if task hasn't moved between sections
        if (newSectionId === oldSectionId) {
          return;
        }

        // Handle overdue section - tasks cannot be moved into overdue
        if (newSectionId === DayPartSections.Overdue) {
          return;
        }

        // Get the new timeslot startTime for the dayPart
        const newStartTime = getDayPartStartTime(newSectionId);

        // Handle scheduled tasks (habits, repeating tasks, scheduled one-off)
        if (task.schedules?.length) {
          const planning = getDueTaskPlanning(task, {
            date: today,
            today,
            weekStartsOn,
          });
          const plannedToday = planning.find(({ date }) =>
            isSameDay(date, today),
          );
          const overduePlannedOccurrence = plannedToday
            ? undefined
            : planning.find(({ date }) => isBefore(date, today));

          const newTimeslot = {
            startTime: newStartTime,
            duration:
              plannedToday?.timeslot?.duration ??
              overduePlannedOccurrence?.timeslot?.duration ??
              halfHour,
          };

          taskToUpdate.schedules = createPlannedOccurrenceForDate(
            task.schedules,
            today,
            {
              timeslot: newTimeslot,
              originalDate:
                overduePlannedOccurrence?.date ?? plannedToday?.date ?? today,
              weekStartsOn,
              today,
            },
          );
          updatedDocs.push(taskToUpdate);
          return;
        }

        // Non-scheduled task: ensure deadline is today and apply the new timeslot
        const newTimeslot = {
          startTime: newStartTime,
          duration: task.timeslot?.duration ?? halfHour,
        };
        taskToUpdate.endStrategy = {
          deadline: endOfDay(today),
          completionCount: task.endStrategy?.completionCount ?? null,
        };
        taskToUpdate.timeslot =
          newStartTime || newTimeslot.duration ? newTimeslot : null;
        updatedDocs.push(taskToUpdate);
      }

      // Collect section updates for custom order
      if (item.type === TreeItemTypes.DayPart) {
        updatedSections.push({
          id: item.id,
          taskSorting: item.children
            ? item.children.filter((childId) => !childId.endsWith('-new-task'))
            : [],
        });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TreeItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          const parentElement = itemInstance.getParent();
          const parentSectionId = parentElement?.getItemData().sourceId;
          const displayDue =
            parentSectionId === DayPartSections.Overdue
              ? undefined
              : groupSettings.displayDue;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              displayDue={displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TreeItemTypes.NewTask) {
          const sectionId = item.sourceId.replace('-new-task', '');
          const startTime = getDayPartStartTime(sectionId as DayPartSections);
          return (
            <NewTaskRow
              startTime={startTime ?? null}
              duration={null}
              date={defaultNewTaskDate}
              lifeArea={groupSettings.lifeAreaId}
              tags={groupSettings.tagId ? [groupSettings.tagId] : undefined}
            />
          );
        }

        // DayPart
        if (type === TreeItemTypes.DayPart) {
          return (
            <DayPartHeader denseFormat={denseFormat} item={itemInstance} />
          );
        }

        return null;
      },
    [
      denseFormat,
      defaultNewTaskDate,
      groupSettings.displayDue,
      groupSettings.lifeAreaId,
      groupSettings.tagId,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskTreeItemTypes}
        dragTypes={taskTreeItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
